import { Dialog, Portal } from "@headlessui/react";
import clsx from "clsx";
import React, { Fragment } from "react";
import { useUnsafeFormContext } from "../form";
import { useTranslation } from "../localization";
import type { ChildrenProps, DataAttributeProps } from "../types";
import { ConfirmModal } from "./confirm-modal";
import { ModalContext } from "./context";
import { ModalBody } from "./modal-body";
import { ModalCloseButton } from "./modal-close-button";
import { ModalContent } from "./modal-content";
import { ModalFooter } from "./modal-footer";
import { ModalFooterForm } from "./modal-footer-form";
import { ModalHeader } from "./modal-header";
import { ModalLoading } from "./modal-loading";
import { ModalOverlay } from "./modal-overlay";
import { ModalTableWrapper } from "./modal-table-wrapper";
import { useAsyncModal } from "./use-async-modal";

/** Props Interface */
interface _ModalProps extends ChildrenProps, DataAttributeProps {
	isOpen: boolean;
	onClose: () => void;
	title: string | null;
	onSubmit?: () => void;
	isDraggable?: boolean;
	isLoading?: boolean;
	closeOnEsc?: boolean;
	width?: string;
	withWarning?: boolean;
	withOverlay?: boolean;
}

export type ModalProps = Pick<_ModalProps, "isOpen" | "onClose">;

export function Modal({
	isOpen,
	onClose,
	title,
	children,
	onSubmit,
	isDraggable = false,
	closeOnEsc = true,
	isLoading = false,
	width,
	withOverlay = true,
	withWarning = true,
	...props
}: _ModalProps) {
	/** Context */
	const methods = useUnsafeFormContext();
	const asyncModal = useAsyncModal();
	const { t } = useTranslation();

	/** Functions */
	async function handleOnClose() {
		if (!withWarning) {
			onClose();
			return;
		}
		if (asyncModal.isOpen) return;
		if (Object.entries(methods?.formState.dirtyFields || {}).length) {
			if (!methods?.formState.isSubmitted) {
				const isConfirmed = await asyncModal.confirm();
				if (!isConfirmed) return;
			}
		}
		onClose();
	}

	/** Render */
	return (
		<Portal>
			<ModalContext.Provider value={{ isOpen, onClose: handleOnClose, onSubmit }}>
				{isOpen && (
					<Dialog
						className={clsx(
							"z-500 fixed inset-0 flex flex-col justify-start items-center bg-transparent w-screen h-screen",
							"pt-[10vh]",
						)}
						onClose={handleOnClose}
						open={isOpen}
					>
						<Fragment>
							{withOverlay && <ModalOverlay />}
							<ModalContent
								isDraggable={isDraggable}
								closeOnEsc={closeOnEsc}
								width={width}
								data-cy={props["data-cy"] ?? "modal-content"}
							>
								<ModalCloseButton />
								{title && <ModalHeader title={title} />}
								{isOpen ? !isLoading ? children : <ModalLoading /> : null}
							</ModalContent>
						</Fragment>
					</Dialog>
				)}
			</ModalContext.Provider>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("confirmation.confirm_discard")}
				/>
			)}
		</Portal>
	);
}

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
Modal.FooterForm = ModalFooterForm;
Modal.TableWrapper = ModalTableWrapper;
